"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { SSO_REQUIRED_MESSAGE } from "@/lib/auth/admin-auth-methods.shared";
import { ArrowRight, Mail } from "lucide-react";
import { useState } from "react";
import { resolveEmailLoginRoute, sendOtp } from "../../_logic/auth";
import { OtpVerification } from "./OtpVerification";

interface EmailSignInProps {
  redirectTo?: string;
  onOtpStateChange?: (showOtp: boolean) => void;
}

export const EmailSignIn = ({ redirectTo, onOtpStateChange }: EmailSignInProps) => {
  // States
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showOtp, setShowOtp] = useState<boolean>(false);

  // Helpers
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handlers
  const handleSendOtp = async () => {
    setError(null);
    setIsLoading(true);
    try {
      // Per-org SSO enforcement: if this email's org requires SAML SSO, route to
      // the IdP instead of sending a code. `blocked` = enforced but SSO could not
      // be initiated (fail closed — never fall through to OTP).
      const route = await resolveEmailLoginRoute(email, redirectTo);
      if (route.kind === "sso") {
        window.location.href = route.url;
        return; // navigating away — keep the button in its loading state
      }
      if (route.kind === "blocked") {
        setError(SSO_REQUIRED_MESSAGE);
        return;
      }

      await sendOtp(email, redirectTo);
      setShowOtp(true);
      onOtpStateChange?.(true);
    } catch {
      // Server-action error messages are redacted in production, so show a generic notice.
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setShowOtp(false);
    onOtpStateChange?.(false);
  };

  if (showOtp) {
    return <OtpVerification email={email} onChangeEmail={handleChangeEmail} redirectTo={redirectTo} />;
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-2 w-full">
        <label className="text-sm font-medium text-gray-900">Email address</label>
        <FetchInput
          placeholder="you@company.com"
          className="w-full py-3 rounded-xl"
          leftIcon={<Mail className="size-4 text-gray-300" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && isValidEmail(email)) {
              handleSendOtp();
            }
          }}
        />
        {error && <p className="text-sm text-red-500 font-medium mt-2">{error}</p>}
      </div>
      <FetchButton
        variant="primary"
        color="primary"
        size="lg"
        rightIcon={<ArrowRight className="size-4 text-white" />}
        className="w-full"
        disabled={!isValidEmail(email) || isLoading}
        loading={isLoading}
        onClick={handleSendOtp}
      >
        Send one-time code
      </FetchButton>
    </div>
  );
};
